import React, { useMemo } from 'react';
import { ThemeProvider, createTheme, alpha, lighten, darken } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth } from './contexts/AuthContext';

export const panelPalettes = [
  { id: 'cyan', label: 'Голубой', main: '#64d9ed', secondary: '#ae94f9' },
  { id: 'blue', label: 'Синий', main: '#83adf5', secondary: '#64d9ed' },
  { id: 'indigo', label: 'Индиго', main: '#a3a8ff', secondary: '#83adf5' },
  { id: 'violet', label: 'Фиолетовый', main: '#bd9af7', secondary: '#ef9cd2' },
  { id: 'pink', label: 'Розовый', main: '#ef9cd2', secondary: '#bd9af7' },
  { id: 'red', label: 'Красный', main: '#f28b92', secondary: '#f3b180' },
  { id: 'orange', label: 'Оранжевый', main: '#f3b180', secondary: '#f0ce76' },
  { id: 'amber', label: 'Янтарный', main: '#f0ce76', secondary: '#a8d88a' },
  { id: 'green', label: 'Зелёный', main: '#8ed49b', secondary: '#72d3bd' },
  { id: 'teal', label: 'Бирюзовый', main: '#72d3bd', secondary: '#83adf5' },
];

export const panelBackgrounds = [
  { id: 'midnight', label: 'Полночь', main: '#090c14' },
  { id: 'graphite', label: 'Графитовый', main: '#191b20' },
  { id: 'navy', label: 'Морской', main: '#101c32' },
  { id: 'indigo', label: 'Индиго', main: '#1b1836' },
  { id: 'plum', label: 'Сливовый', main: '#25172e' },
  { id: 'burgundy', label: 'Бордовый', main: '#2b151e' },
  { id: 'forest', label: 'Лесной', main: '#10271c' },
  { id: 'teal', label: 'Бирюзовый', main: '#0c2529' },
  { id: 'coffee', label: 'Кофейный', main: '#261c14' },
  { id: 'black', label: 'Чёрный', main: '#050505' },
  { id: 'sapphire', label: 'Сапфир', main: '#17348f', vivid: true },
  { id: 'amethyst', label: 'Аметист', main: '#502087', vivid: true },
  { id: 'fuchsia', label: 'Фуксия', main: '#69165f', vivid: true },
  { id: 'ruby', label: 'Рубин', main: '#771336', vivid: true },
  { id: 'copper', label: 'Медный', main: '#662906', vivid: true },
  { id: 'gold', label: 'Золотой', main: '#503800', vivid: true },
  { id: 'emerald', label: 'Изумруд', main: '#06462b', vivid: true },
  { id: 'lagoon', label: 'Лагуна', main: '#064359', vivid: true },
  { id: 'cobalt', label: 'Кобальт', main: '#12376d', vivid: true },
  { id: 'iris', label: 'Ирис', main: '#402291', vivid: true },
];

export const panelTextColors = [
  { id: 'default', label: 'Стандартный', main: '#e7edf7' },
  { id: 'white', label: 'Белый', main: '#ffffff' },
  { id: 'silver', label: 'Серебристый', main: '#cbd5e1' },
  { id: 'cream', label: 'Кремовый', main: '#fff1cc' },
  { id: 'mint', label: 'Мятный', main: '#c3f5d9' },
  { id: 'cyan', label: 'Голубой', main: '#bbf0f5' },
  { id: 'blue', label: 'Синий', main: '#c7ddff' },
  { id: 'lavender', label: 'Лавандовый', main: '#e1d0ff' },
  { id: 'pink', label: 'Розовый', main: '#ffd0e6' },
  { id: 'peach', label: 'Персиковый', main: '#ffdbc2' },
];

export const getPanelPalette = id => panelPalettes.find(palette => palette.id === id) || panelPalettes[0];
export const getPanelBackground = id => panelBackgrounds.find(background => background.id === id) || panelBackgrounds[0];
export const getPanelTextColor = id => panelTextColors.find(color => color.id === id) || panelTextColors[0];

// Create a modern theme
export function createPanelTheme(id, backgroundId, textColorId) {
  const { main, secondary } = getPanelPalette(id);
  const background = getPanelBackground(backgroundId);
  const textColor = getPanelTextColor(textColorId);
  const original = background.id === 'midnight';
  const surfaceBase = background.vivid ? darken(background.main, 0.28) : background.main;
  const secondaryText = textColor.id === 'default' ? (background.vivid ? '#bdc9dc' : '#919db2') : darken(textColor.main, 0.12);
  const headingText = textColor.id === 'default' ? '#a9b4c9' : secondaryText;
  const surfaces = {
    default: background.main,
    paper: original ? '#121722' : lighten(surfaceBase, 0.035),
    sidebar: original ? '#0e121d' : lighten(surfaceBase, 0.02),
    input: original ? '#0e131e' : lighten(surfaceBase, 0.025),
    elevated: original ? '#191f2e' : lighten(surfaceBase, 0.06),
  };
  const divider = original ? '#273044' : lighten(background.main, 0.13);
  const border = original ? '#303c52' : lighten(background.main, 0.17);
  const borderHover = original ? '#51637f' : lighten(background.main, 0.28);
  return createTheme({
    palette: {
      mode: 'dark',
      primary: { main, contrastText: '#061b24' },
      secondary: { main: secondary, contrastText: '#190e30' },
      success: { main: '#6fcca7', contrastText: '#0c261d' },
      warning: { main: '#e6b873', contrastText: '#2a1d08' },
      error: { main: '#ef889d', contrastText: '#300f1a' },
      info: { main: '#83adf5', contrastText: '#101f33' },
      background: surfaces,
      text: { primary: textColor.main, secondary: secondaryText, disabled: '#647086' },
      divider,
      action: {
        hover: alpha(main, 0.05),
        selected: alpha(main, 0.10),
        disabledBackground: original ? '#202737' : lighten(background.main, 0.08),
        disabled: '#748096',
      },
    },
    typography: {
      fontFamily: '"Segoe UI", "Helvetica Neue", sans-serif',
      fontSize: 13,
      h1: { fontWeight: 750, letterSpacing: '-0.055em' },
      h2: { fontWeight: 750, letterSpacing: '-0.045em' },
      h3: { fontWeight: 750, letterSpacing: '-0.04em' },
      h4: { fontWeight: 750, letterSpacing: '-0.035em', fontSize: '1.85rem' },
      h5: { fontWeight: 700, letterSpacing: '-0.025em' },
      h6: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.05rem' },
      body1: { lineHeight: 1.65 },
      body2: { lineHeight: 1.6 },
      overline: { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.14em' },
      button: { textTransform: 'none', fontWeight: 700 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { colorScheme: 'dark', '--panel-primary': main, '--panel-primary-contrast': '#061b24', '--panel-input': surfaces.input, '--panel-text': textColor.main },
          '::selection': { background: alpha(main, 0.25), color: textColor.main },
          '*': { scrollbarWidth: 'thin', scrollbarColor: `${border} transparent` },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, padding: '9px 17px', transition: 'background-color 160ms, border-color 160ms, box-shadow 160ms' },
          containedPrimary: { boxShadow: `0 0 16px ${alpha(main, 0.07)}`, '&:hover': { boxShadow: `0 0 22px ${alpha(main, 0.16)}` } },
          outlined: { borderColor: border },
          sizeSmall: { padding: '5px 11px' },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 9 } },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' }, rounded: { borderRadius: 12 } },
      },
      MuiCard: {
        styleOverrides: { root: { border: `1px solid ${divider}`, boxShadow: 'none', backgroundImage: 'none' } },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 22, '&:last-child': { paddingBottom: 22 } } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: surfaces.input, borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderHover },
            '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(main, 0.05)}` },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1 },
          },
          notchedOutline: { borderColor: border },
          input: { fontSize: '0.875rem' },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: '0.875rem' } } },
      MuiSelect: { styleOverrides: { icon: { color: secondaryText } } },
      MuiMenu: {
        styleOverrides: { paper: { border: `1px solid ${border}`, backgroundColor: surfaces.elevated, boxShadow: '0 16px 48px #0006' } },
      },
      MuiPopover: { styleOverrides: { paper: { border: `1px solid ${border}`, backgroundImage: 'none' } } },
      MuiMenuItem: { styleOverrides: { root: { fontSize: '0.875rem', minHeight: 40 } } },
      MuiDialog: {
        styleOverrides: { paper: { border: `1px solid ${border}`, boxShadow: '0 24px 100px #0008', backgroundImage: 'none' } },
      },
      MuiDialogTitle: { styleOverrides: { root: { padding: '22px 24px', borderBottom: `1px solid ${divider}` } } },
      MuiDialogContent: { styleOverrides: { root: { paddingTop: 24, '&.MuiDialogContent-root': { paddingTop: 24 } } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', borderTop: `1px solid ${divider}`, gap: 8 } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: divider, padding: '14px 16px', fontVariantNumeric: 'tabular-nums' },
          head: { backgroundColor: surfaces.elevated, color: headingText, fontWeight: 650, fontSize: '0.75rem' },
        },
      },
      MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: alpha(main, 0.03) } } } },
      MuiTablePagination: { styleOverrides: { root: { color: secondaryText } } },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            borderColor: divider, borderRadius: 10, fontVariantNumeric: 'tabular-nums',
            '--DataGrid-containerBackground': surfaces.elevated,
            '--DataGrid-pinnedBackground': surfaces.paper,
            '& .MuiDataGrid-columnHeaders': { color: headingText, fontSize: '0.75rem' },
            '& .MuiDataGrid-cell': { borderColor: divider },
            '& .MuiDataGrid-footerContainer': { borderColor: divider },
            '& .MuiDataGrid-toolbarContainer': { padding: 12, gap: 8 },
            '& .MuiDataGrid-row:hover': { backgroundColor: alpha(main, 0.03) },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 6, fontWeight: 650 }, sizeSmall: { height: 24, fontSize: '0.7rem' } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10, border: '1px solid currentColor', alignItems: 'center' } } },
      MuiTabs: { styleOverrides: { root: { borderBottom: `1px solid ${divider}` }, indicator: { height: 2, boxShadow: `0 0 10px ${alpha(main, 0.33)}` } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 650, minHeight: 48 } } },
      MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: surfaces.elevated, color: textColor.main, border: `1px solid ${border}`, fontSize: '0.75rem', padding: '8px 12px' } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: divider } } },
    },
  });
}

export function PanelThemeProvider({ children }) {
  const { currentUser } = useAuth();
  const palette = currentUser?.panelPalette;
  const background = currentUser?.panelBackground;
  const textColor = currentUser?.panelTextColor;
  const theme = useMemo(() => createPanelTheme(palette, background, textColor), [palette, background, textColor]);
  return <ThemeProvider theme={theme}><CssBaseline />{children}</ThemeProvider>;
}
